import { useCallback, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import {
  EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING,
  EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_VERIFIED
} from '@klear/bank-account/constants'
import {
  ROUTE_EXTERNAL_BANK,
  PERMISSION_ENTITY_TREASURY,
  PERMISSION_ACTION_EDIT,
  ROUTE_KLEAR_ACCOUNT,
  ROUTE_MAKE_PAYMENT,
  buildNavPathTrader
} from '@klear/constants'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useTrader } from '@klear/ui/hooks/use-trader'
import {
  useTraderExternalBankAccounts,
  useTraderExternalBankAccountMutations
} from '@klear/ui/hooks/use-trader-external-bank-accounts'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import toInteger from 'lodash/toInteger'
import {
  externalBankAccountFormToCreateWire,
  externalBankAccountFormToUpdateWire
} from '@klear/bank-account/wire.client'
import { EntityWizardShell } from '@klear/ui/wizard/entity-shell'
import { returnUrlFromSearchParams, useReturnUrlExit } from '@klear/ui/wizard/return-url'
import {
  ExternalBankAccountFlow,
  type ExternalBankAccountCommitArgs,
  type ExternalBankAccountFlowMode
} from '@/app/external-bank-account/shared/external-bank-account-step-group'

export interface TraderExternalBankAccountWizardPageProps {
  mode: ExternalBankAccountFlowMode
}

export default function TraderExternalBankAccountWizardPage({ mode }: TraderExternalBankAccountWizardPageProps) {
  const { traderId, externalBankAccountId } = useParams<{
    traderId?: string
    externalBankAccountId?: string
  }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { trader, urlPrefixApp } = useTrader(traderId)
  const accountIdNum = externalBankAccountId ? toInteger(externalBankAccountId) : null
  const canEdit = !!user?.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT)

  const baseList = `${urlPrefixApp}/${ROUTE_EXTERNAL_BANK}`
  const baseWorkflow = baseList

  const { traderExternalBankAccounts, isLoading } = useTraderExternalBankAccounts()
  const { commitTraderExternalBankAccount, isCommitting } = useTraderExternalBankAccountMutations()
  const account = useMemo(() => {
    if (mode === 'create' || accountIdNum == null) return null
    return traderExternalBankAccounts.find((a) => a.id === accountIdNum) ?? null
  }, [traderExternalBankAccounts, mode, accountIdNum])
  useDocumentTitle(
    account?.nameAndLast4,
    mode === 'create' ? 'New Bank Account' : mode === 'edit' ? 'Edit Bank Account' : 'Bank Account'
  )

  const isAccountLocked =
    mode === 'edit' &&
    (account?.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING ||
      account?.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_VERIFIED ||
      account?.traderLegalAgreementAcceptanceId != null)

  const { exit, exitAfterSave } = useReturnUrlExit(baseList)
  const goToEdit = useCallback(() => {
    if (accountIdNum != null) navigate(`${baseWorkflow}/${accountIdNum}/edit`)
  }, [navigate, baseWorkflow, accountIdNum])

  const navigateAfterSave = useCallback(() => {
    if (returnUrlFromSearchParams(searchParams) != null) {
      exitAfterSave()
      return
    }
    if (searchParams.get('returnToPayment') === 'true' && traderId) {
      const network = searchParams.get('network')
      const supplierParam = searchParams.get('supplierId')
      const qs = new URLSearchParams()
      if (supplierParam) qs.set('supplierId', supplierParam)
      if (network) qs.set('network', network)
      navigate(
        `${buildNavPathTrader(traderId, ROUTE_KLEAR_ACCOUNT, ROUTE_MAKE_PAYMENT)}${qs.toString() ? `?${qs.toString()}` : ''}`
      )
      return
    }
    exitAfterSave()
  }, [searchParams, traderId, navigate, exitAfterSave])

  const onCommit = useCallback(
    async (args: ExternalBankAccountCommitArgs) => {
      if (args.mode === 'create') {
        const result = await commitTraderExternalBankAccount({
          data: externalBankAccountFormToCreateWire(args.formValues)
        })
        return result
      }
      if (args.accountId == null) throw new Error('Missing bank account id')
      await commitTraderExternalBankAccount({
        id: args.accountId,
        data: externalBankAccountFormToUpdateWire(args.formValues, args.dirtyFields)
      })
    },
    [commitTraderExternalBankAccount]
  )

  const title = mode === 'create' ? 'Add bank account' : (account?.nameAndLast4 ?? 'Bank account')

  return (
    <div className="h-[calc(100vh-4rem)]">
      <EntityWizardShell
        mode={mode}
        entityLabel="bank account"
        title={title}
        canEdit={canEdit}
        isLoading={isLoading}
        notFound={mode !== 'create' && !account}
        notFoundLabel="Bank account not found"
        onBack={exit}
        onEdit={goToEdit}
        isBusy={isCommitting}
      >
        {({ activeMode, onNavStateChange, cancelEdit, requestExit, onDirtyChange }) => (
          <ExternalBankAccountFlow
            key={`${activeMode}:${accountIdNum ?? 'new'}`}
            mode={activeMode}
            accountId={accountIdNum ?? undefined}
            account={account}
            addressSeed={trader?.address ?? null}
            isAccountLocked={!!isAccountLocked}
            isCommitting={isCommitting}
            onNavStateChange={onNavStateChange}
            onDirtyChange={onDirtyChange}
            onComplete={navigateAfterSave}
            onCancel={activeMode === 'create' ? requestExit : cancelEdit}
            onCommit={onCommit}
          />
        )}
      </EntityWizardShell>
    </div>
  )
}
